import type { FontTable } from '@d2/formats';
import type { Sprite } from '../gfx/atlas';
import type { Renderer } from '../gfx/renderer';
import { type Assets, TextColor } from './store';

export type Align = 'left' | 'center' | 'right';

export interface TextOpts {
  color?: TextColor;
  align?: Align;
  alpha?: number;
  /** Max width for word wrapping. */
  wrap?: number;
  lineHeight?: number;
  palette?: number;
}

const COLOR_CODE = 'ÿc';
const CODE_CHARS = '0123456789:;<';

/** A D2 bitmap font: DC6 glyph frames + TBL metrics. Supports `ÿcN` color codes. */
export class Font {
  readonly lineHeight: number;
  constructor(
    private readonly assets: Assets,
    readonly table: FontTable,
    private readonly glyphs: Sprite[],
  ) {
    this.lineHeight = table.lineHeight;
  }

  private advance(code: number): number {
    return this.table.glyphs.get(code)?.width ?? this.table.glyphs.get(32)?.width ?? 4;
  }

  /** Width of a single line, ignoring color codes. */
  width(text: string): number {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
      if (text.startsWith(COLOR_CODE, i)) {
        i += 2;
        continue;
      }
      w += this.advance(text.charCodeAt(i));
    }
    return w;
  }

  /** Splits into lines, wrapping at `maxWidth` on spaces. Explicit `\n` always breaks. */
  lines(text: string, maxWidth?: number): string[] {
    const out: string[] = [];
    for (const para of text.split('\n')) {
      if (!maxWidth) {
        out.push(para);
        continue;
      }
      let line = '';
      for (const word of para.split(' ')) {
        const next = line ? `${line} ${word}` : word;
        if (line && this.width(next) > maxWidth) {
          out.push(line);
          line = word;
        } else line = next;
      }
      out.push(line);
    }
    return out;
  }

  measure(text: string, maxWidth?: number, lineHeight = this.lineHeight): { w: number; h: number } {
    const ls = this.lines(text, maxWidth);
    return { w: Math.max(0, ...ls.map((l) => this.width(l))), h: ls.length * lineHeight };
  }

  /** Draws text with its top-left (or top-center/right) at (x, y). */
  draw(r: Renderer, text: string, x: number, y: number, o: TextOpts = {}): void {
    const lh = o.lineHeight ?? this.lineHeight;
    const lines = this.lines(text, o.wrap);
    let color = o.color ?? TextColor.White;
    lines.forEach((line, li) => {
      const w = this.width(line);
      let cx = o.align === 'center' ? Math.round(x - w / 2) : o.align === 'right' ? x - w : x;
      const cy = y + li * lh;
      for (let i = 0; i < line.length; i++) {
        if (line.startsWith(COLOR_CODE, i)) {
          const c = CODE_CHARS.indexOf(line[i + 2]);
          if (c >= 0) color = c as TextColor;
          i += 2;
          continue;
        }
        const code = line.charCodeAt(i);
        const g = this.table.glyphs.get(code);
        if (g && g.frame < this.glyphs.length) {
          const remap = color === TextColor.White ? undefined : this.assets.textRemaps[color];
          r.draw(this.glyphs[g.frame], cx, cy, { topLeft: true, remap, alpha: o.alpha, palette: o.palette });
        }
        cx += this.advance(code);
      }
    });
  }
}
